import { useCallback, useEffect, useState } from "react";
import { api, type AuditEntry } from "../api";
import { ErrorNote, errMsg } from "../ui";

const ENTITY_LABEL: Record<string, string> = {
  ai_systems: "AI system", control_status: "Control", memberships: "Membership", tenants: "Workspace", register: "Register",
};
const HIDDEN = new Set(["tenant_id", "created_at", "updated_at", "created_by", "updated_by", "version", "invited_by", "user_id"]);

function Diff({ e }: { e: AuditEntry }) {
  const b = e.before ?? {}, a = e.after ?? {};
  const keys = [...new Set([...Object.keys(b), ...Object.keys(a)])].filter((k) => !HIDDEN.has(k) && JSON.stringify(b[k]) !== JSON.stringify(a[k]));
  if (!keys.length) return null;
  const show = (v: unknown) => (v === undefined || v === null ? "" : typeof v === "string" ? v : JSON.stringify(v));
  return (
    <div className="diff">{keys.map((k) => (
      <div key={k} style={{ display: "contents" }}>
        <span className="muted">{k}</span>
        <span>{e.action !== "insert" && b[k] !== undefined && <del>{show(b[k])}</del>}{e.action !== "insert" && b[k] !== undefined && e.action !== "delete" && " → "}{e.action !== "delete" && <ins>{show(a[k])}</ins>}</span>
      </div>))}
    </div>
  );
}

export function AuditView({ tenantId }: { tenantId: string }) {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [next, setNext] = useState<number | null>(null);
  const [entity, setEntity] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [check, setCheck] = useState<{ ok: boolean; checked: number; firstBadId: number | null } | null>(null);

  const load = useCallback(async (before?: number) => {
    const qs = new URLSearchParams({ limit: "50" });
    if (before) qs.set("before", String(before));
    if (entity) qs.set("entity", entity);
    try {
      const r = await api<{ entries: AuditEntry[]; nextBefore: number | null }>("GET", `/tenants/${tenantId}/audit?${qs}`);
      setEntries((cur) => (before ? [...cur, ...r.entries] : r.entries));
      setNext(r.nextBefore);
    } catch (e) { setError(errMsg(e)); }
  }, [tenantId, entity]);

  useEffect(() => { void load(); }, [load]);

  return (
    <div className="stack">
      <section className="panel">
        <div className="row spread">
          <div><h2>Audit trail</h2><p className="muted small" style={{ margin: "4px 0 0", maxWidth: "70ch" }}>
            Every change to this workspace, recorded by the database in the same transaction. Entries cannot be edited or deleted, and each is hash-chained to the one before.</p></div>
          <div className="row">
            <select className="input" id="audit-entity" value={entity} onChange={(e) => setEntity(e.target.value)} style={{ width: "auto" }}>
              <option value="">All changes</option><option value="ai_systems">AI systems</option><option value="control_status">Controls</option>
              <option value="memberships">Members</option><option value="tenants">Workspace</option>
            </select>
            <button className="btn" onClick={async () => {
              try { setCheck(await api("GET", `/tenants/${tenantId}/audit/verify`)); } catch (e) { setError(errMsg(e)); }
            }}>Verify integrity</button>
          </div>
        </div>
        {check && <p className={check.ok ? "ok-line" : "bad-line"} role="status">
          {check.ok ? `Chain intact: ${check.checked} entries verified.` : `Chain broken at entry ${check.firstBadId}. The log was altered outside the application.`}</p>}
      </section>
      <ErrorNote error={error} />
      <section className="panel">
        {entries.length === 0 ? <p className="muted">No entries yet.</p> : (
          <div className="scroll"><table>
            <thead><tr><th>#</th><th>When</th><th>Who</th><th>What</th></tr></thead>
            <tbody>{entries.map((e) => (
              <tr key={e.id}>
                <td className="mono">{e.id}</td>
                <td className="small" style={{ whiteSpace: "nowrap" }}>{new Date(e.at).toLocaleString("en-GB")}</td>
                <td className="small">{e.actor || "System"}</td>
                <td><strong>{e.action[0]!.toUpperCase() + e.action.slice(1)}</strong> {ENTITY_LABEL[e.entity] ?? e.entity}
                  {e.entity === "control_status" && <span className="mono muted"> {e.entityId.split(":")[1]}</span>}
                  {(e.after?.name ?? e.before?.name) ? <span className="muted"> · {String(e.after?.name ?? e.before?.name)}</span> : null}
                  {e.entity === "memberships" && <span className="muted"> · {String(e.after?.email ?? e.before?.email)}</span>}
                  {e.entity === "memberships" && e.action === "update" && !e.before?.user_id && e.after?.user_id
                    ? <div className="muted small">Invitation accepted on first sign-in</div> : null}
                  <Diff e={e} /></td>
              </tr>))}
            </tbody>
          </table></div>
        )}
        {next && <button className="btn" style={{ marginTop: 12 }} onClick={() => load(next)}>Load older entries</button>}
      </section>
    </div>
  );
}
